<template>
  <hit-base-input
    :label="baseInputLabel"
    :floating-label="useFloatingLabel"
    :validation-state="validationState"
    :inline-input="effectiveInlineInput"
    :inline-label-width="inlineLabelWidth"
    :inline-label-color-visible="inlineLabelColorVisible"
    :reset-validation-if-valid="resetValidationIfValid"
    :full-width="false"
    :only-show-error-border="effectiveInlineInput || useFloatingLabel"
    :disabled="disabled"
    :label-alignment-side="labelAlignmentSide"
    :help="useFloatingLabel ? null : help"
    :add-bottom-margin="addBottomMargin"
    :compact-input-layout="(isDate || isDateTime) && !!widthClass"
  >
    <div
      :style="inputStyle"
      :class="{
        'grid input': effectiveInlineInput || extraSlotVisible,
        'items-center inline-grid': isCheckbox,
        'cursor-not-allowed': disabled || readonly,
        'flex flex-row items-center':
          originalData ||
          ((hasInlineIcon || extraSlotVisible) && !effectiveInlineInput),
      }"
      class="h-full"
    >
      <div
        :class="{
          'h-full text-center items-center flex':
            disabled && displayLockIfDisabled && !(isDate || isDateTime),
        }"
      >
        <div
          v-if="disabled && !(isDate || isDateTime) && displayLockIfDisabled"
          :class="inputClasses"
          class="w-8 px-1 h-full shrink-0 flex flex-row items-center justify-center border rounded-l border-r-0 rounded-r-none"
        >
          <hit-icon icon="lock" class="justify-center items-center" />
        </div>
      </div>

      <div
        v-if="useFloatingLabel"
        class="hit-floating-wrap relative h-full min-w-0 flex-1 self-stretch"
        :class="[
          floatingFieldWidthClass,
          {'hit-floating-wrap--date': isDate || isDateTime},
        ]"
      >
        <span
          v-if="showFloatingError"
          :class="[
            ...floatingErrorLabelClassList,
            disabled && displayLockIfDisabled && !(isDate || isDateTime)
              ? 'hit-floating-label--with-left-lock'
              : null,
          ]"
          :style="floatingErrorLabelStyle"
          aria-live="polite"
        >
          <span class="hit-floating-label__text">{{
            floatingErrorMessage
          }}</span>
          <hit-floating-label-help
            v-if="floatingHelpVisible && !showFloatingCaption"
          >
            <slot name="help">
              <hit-help v-if="help" :text="help" floating />
            </slot>
          </hit-floating-label-help>
        </span>
        <div
          v-if="showFloatingCaption"
          class="hit-floating-label"
          :class="[
            ...floatingLabelClassList,
            disabled && displayLockIfDisabled && !(isDate || isDateTime)
              ? 'hit-floating-label--with-left-lock'
              : null,
          ]"
        >
          <span class="hit-floating-label__text">
            {{ floatingLabelText }}
            <span
              v-if="pristine && required && invalid"
              class="text-danger-icon"
              >*</span
            >
          </span>
          <hit-floating-label-help v-if="floatingHelpVisible">
            <slot name="help">
              <hit-help v-if="help" :text="help" floating />
            </slot>
          </hit-floating-label-help>
        </div>
        <input
          :id="inputId"
          ref="input"
          max="9999-12-31"
          :class="floatingInputClassList"
          :value="inputValue"
          :type="inputType"
          :inputmode="inputMode"
          :step="step"
          :disabled="isCheckbox && disabled"
          :readonly="!isCheckbox && (disabled || readonly)"
          :aria-disabled="disabled ? 'true' : undefined"
          :placeholder="resolvedPlaceholder"
          :autocomplete="autocomplete"
          @input="onInput"
          @change="onChange"
          @focus="onFocus"
          @keydown="onKeydown"
          @keyup.enter="(evt) => $emit('keyup.enter', evt.target.value)"
          @blur="onBlur"
        />
      </div>
      <input
        v-else
        :id="inputId"
        ref="input"
        max="9999-12-31"
        :class="{
          ...inputClasses,
          'focus:border-input-focus': !erroneous,
          'focus:border-danger': erroneous,
          'lg:w-auto':
            effectiveInlineInput && !isCheckbox && !(isDate || isDateTime),
          [widthClass]: widthClass !== null,
          'w-full': !widthClass && !isCheckbox,
          'border focus:ring-transparent rounded h-full focus:outline-none':
            !isCheckbox,
          'mt-1': isCheckbox,
          'ml-1': isCheckbox && label,
          [computedTextAlignment]: computedTextAlignment,
          [customBackground]: customBackground,
          'cursor-not-allowed': disabled || readonly,
          'hit-input-muted-text': disabled || readonly,
          'border-r-0 rounded-r-none':
            hasSuffixIcon ||
            originalData ||
            showTogglePassword ||
            extraSlotVisible,
          'border-l-0 rounded-l-none': disabled && displayLockIfDisabled,
          'hide-number-spinners': hideNumberSpinners,
        }"
        :value="inputValue"
        :type="inputType"
        :inputmode="inputMode"
        :step="step"
        :disabled="isCheckbox && disabled"
        :readonly="!isCheckbox && (disabled || readonly)"
        :aria-disabled="disabled ? 'true' : undefined"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        @input="onInput"
        @change="onChange"
        @focus="onFocus"
        @keydown="onKeydown"
        @keyup.enter="(evt) => $emit('keyup.enter', evt.target.value)"
        @blur="onBlur"
      />
      <div
        v-if="originalData"
        class="h-full shrink-0 self-stretch text-center items-center flex flex-row cursor-default"
      >
        <div
          :title="originalData"
          class="w-8 px-1 h-full flex flex-row border rounded-r border-l-0"
          :class="inputClasses"
        >
          <hit-icon
            icon="reset_changes"
            class="justify-center items-center"
            clickable
            @click="resetToOrignalData"
          />
        </div>
      </div>

      <div
        v-if="hasSuffixIcon"
        class="h-full shrink-0 self-stretch text-center items-center flex"
      >
        <div
          :class="inputClasses"
          class="w-8 px-0 h-full flex flex-row items-center justify-center gap-0.5 border rounded-r border-l-0"
        >
          <hit-icon
            v-if="showTogglePassword"
            :icon="inputType === 'password' ? 'show-on' : 'show-off'"
            class="justify-center items-center"
            clickable
            @click="onPasswordVisibilitySwitch"
          />
          <hit-icon
            v-else-if="showUrlRedirectIcon"
            icon="link"
            class="justify-center items-center"
            clickable
            @click.stop="openUrl"
          />
          <hit-icon
            v-else-if="rightIcon"
            :icon="rightIcon"
            :color="rightIconColor"
            class="justify-center items-center"
            :clickable="
              rightIcon == 'location' ||
              rightIcon == 'info' ||
              rightIcon == 'copy' ||
              !disabled
            "
            :disabled="
              disabled &&
              rightIcon !== 'location' &&
              rightIcon !== 'info' &&
              rightIcon !== 'copy'
            "
            :tooltip="rightIconTooltip"
            @click="onRightIconClick"
          />
          <hit-icon
            v-if="errorIcon"
            icon="error"
            class="justify-center items-center"
            color="danger"
            :tooltip="errorIconTooltip"
          />
        </div>
      </div>
      <!-- @slot Slot to add extra code on the right of the input -->
      <slot name="extra" />
    </div>
    <template v-if="!useFloatingLabel" #help>
      <!-- @slot Help of the input -->
      <slot name="help" />
    </template>
    <template v-for="({}, slot) of nonHelpSlots" :key="slot" #[slot]="scope">
      <slot :name="slot" v-bind="scope" />
    </template>
  </hit-base-input>
</template>

<script>
import HitBaseInput from './HitBaseInput';
import HitFormValidationMixin from '../../../mixins/form/HitFormValidationMixin';
import HitInputMixin from '../../../mixins/form/HitInputMixin';
import HitFloatingLabelMixin from '../../../mixins/form/HitFloatingLabelMixin';
import {useI18n} from 'vue-i18n';
import {HitIcon} from '../../icon';
import HitHelp from '../../help/HitHelp.vue';
import HitFloatingLabelHelp from './HitFloatingLabelHelp.vue';
import {HitBreakpointsMixin} from '../../../mixins';
import {getValidationErrors} from '../../../models/hit/common/validation';
import {Decimal} from 'decimal.js';

export default {
  name: 'HitInput',
  components: {HitIcon, HitBaseInput, HitHelp, HitFloatingLabelHelp},
  mixins: [
    HitFormValidationMixin,
    HitInputMixin,
    HitFloatingLabelMixin,
    HitBreakpointsMixin,
  ],
  props: {
    /**
     * Type of the input
     */
    type: {
      type: String,
      default: 'text',
    },
    /**
     * Value of the input
     */
    value: {
      type: [String, Date, Number],
      required: false,
      default: null,
    },
    /**
     * Disable the input
     */
    disabled: Boolean,
    /**
     * Placeholder of the input
     */
    placeholder: {
      type: String,
      default: null,
    },
    /**
     * Size of the extra slot
     */
    extraSlotSize: {
      type: String,
      default: 'max-content',
    },
    textAlignment: {
      type: String,
      required: false,
      default: null,
    },
    widthClass: {
      type: String,
      required: false,
      default: null,
    },
    /** Set to true to focus the input when the page finished loading **/
    instantFocus: {
      type: Boolean,
      default: false,
    },
    showTogglePassword: {
      type: Boolean,
      default: false,
      required: false,
    },
    resetAfterChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Set step to 1 second for datetime inputs (instead of 1 minute)
     */
    showSeconds: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * When set to true, the entire content is selected when focussing the input
     */
    selectEntireContentOnFocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Icon to display on the right side of the input with border styling similar to HitFastModeBrowser
     */
    rightIcon: {
      type: String,
      default: null,
    },
    rightIconTooltip: {
      type: String,
      default: null,
    },
    rightIconColor: {
      type: String,
      default: null,
    },
    /**
     * When true and the input has a non-empty value, shows a redirect icon
     * that opens the current value as a URL in a new browser tab.
     */
    isUrl: {
      type: Boolean,
      default: false,
    },
    /**
     * Characters kept while typing. Anything else is removed.
     * Value is the inside of a regex character class, e.g. "A-Za-z0-9@;".
     */
    allowedCharacters: {
      type: String,
      default: null,
    },
    /** Value of the input before it has been changed for the first time (for validation flow for example) */
    originalData: {
      type: [String, Object],
      required: false,
      default: null,
    },
    labelAlignmentSide: {
      type: String,
      required: false,
      default: 'right',
      validator: (value) => {
        return ['right', 'left', 'center'].indexOf(value) !== -1;
      },
    },
    displayLockIfDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    autocomplete: {
      type: String,
      required: false,
      default: null,
    },
  },
  setup() {
    const {t, n} = useI18n();
    let passwordIsVisible = false;
    return {t, passwordIsVisible, n};
  },
  data() {
    return {
      userTypedZero: false,
      isFocussed: false,
      inputDecimalPlaces: 0,
      inputValue: null,
      lastCommittedDateInput: null,
      dateUserEdited: false,
      inputId: `hit-input-${this._uid}`,
    };
  },
  computed: {
    recordId() {
      return this.$route.params.id;
    },
    useFloatingLabel() {
      return (
        this.floatingLabel &&
        (!!this.label || !!this.placeholder) &&
        !this.isCheckbox &&
        !this.effectiveInlineInput
      );
    },
    floatingHelpVisible() {
      return !!this.$slots.help || !!this.help;
    },
    nonHelpSlots() {
      const slots = {};
      for (const name of Object.keys(this.$slots)) {
        if (name !== 'help') {
          slots[name] = this.$slots[name];
        }
      }
      return slots;
    },
    floatingFieldWidthClass() {
      return {
        'lg:w-auto':
          this.effectiveInlineInput &&
          !this.isCheckbox &&
          !(this.isDate || this.isDateTime),
        [this.widthClass]: this.widthClass !== null,
        // Without an explicit width, fill the available space (incl. date / datetime)
        'w-full':
          !this.widthClass &&
          !this.isCheckbox &&
          !this.hasSuffixIcon &&
          !this.originalData &&
          !this.extraSlotVisible,
      };
    },
    floatingInputClassList() {
      return {
        ...this.inputClasses,
        'focus:border-input-focus': !this.erroneous,
        'focus:border-danger': this.erroneous,
        'w-full': true,
        'border focus:ring-transparent rounded h-full focus:outline-none':
          !this.isCheckbox,
        [this.computedTextAlignment]: this.computedTextAlignment,
        'cursor-not-allowed': this.disabled || this.readonly,
        'border-r-0 rounded-r-none':
          this.hasSuffixIcon ||
          this.originalData ||
          this.showTogglePassword ||
          this.extraSlotVisible,
        'border-l-0 rounded-l-none':
          this.disabled && this.displayLockIfDisabled,
        'hit-input-muted-text': this.disabled || this.readonly,
        // Original: only hide resting value when not erroneous
        // 'text-transparent':
        //   this.useFloatingLabel && !this.floatingLabelLifted && !this.erroneous,
        // Date fields still paint native segments when erroneous+empty; keep them
        // transparent so they do not clash with the floating error chip.
        'text-transparent':
          this.useFloatingLabel &&
          !this.floatingLabelLifted &&
          (!this.erroneous ||
            ((this.isDate || this.isDateTime) && !this.hasFloatingValue)),
        'hide-number-spinners': this.hideNumberSpinners,
      };
    },
    inputClasses() {
      return {
        'bg-input': true,
        'border-input': !this.erroneous,
        'border-input-focus': this.isFocussed && !this.erroneous,
        'border-danger': this.erroneous,
      };
    },
    /**
     * Native number spinners stay visible on readonly inputs; hide them when
     * type is int and the field is disabled (mapped to readonly on the <input>).
     */
    hideNumberSpinners() {
      return this.type === 'int' && this.disabled;
    },
    computedTextAlignment() {
      if (this.textAlignment) return this.textAlignment;
      if (this.type === 'decimal' || this.type === 'int') return 'text-right';
      return null;
    },
    isDate() {
      return this.type === 'date';
    },
    isDateTime() {
      return this.type === 'datetime-local';
    },
    isTime() {
      return this.type === 'time';
    },
    step() {
      if (this.isDateTime || this.isTime) {
        return this.showSeconds ? 1 : 60;
      } else {
        return 1;
      }
    },
    isText() {
      return this.type === 'text';
    },
    isCheckbox() {
      return this.type === 'checkbox';
    },
    errorIcon() {
      return this.effectiveInlineInput && this.invalid;
    },
    errorIconTooltip() {
      const errors = getValidationErrors(this.validationState);
      let errorMessage = '';
      const translate =
        typeof this.t === 'function'
          ? this.t
          : typeof this.$t === 'function'
            ? this.$t
            : null;
      if (!translate) {
        return errorMessage;
      }
      errors.forEach((error) => {
        errorMessage += translate(error.translationKey, error.params) + '\n';
      });
      return errorMessage;
    },
    showUrlRedirectIcon() {
      if (!this.isUrl) return false;
      const value = this.inputValue ?? this.value;
      return value != null && String(value).trim() !== '';
    },
    hasSuffixIcon() {
      return (
        this.errorIcon ||
        this.rightIcon ||
        this.showTogglePassword ||
        this.showUrlRedirectIcon
      );
    },
    hasInlineIcon() {
      return (
        this.errorIcon ||
        this.rightIcon ||
        this.showTogglePassword ||
        this.showUrlRedirectIcon ||
        (this.disabled && this.displayLockIfDisabled)
      );
    },
    inputType() {
      if (this.type === 'int') {
        return 'number';
      } else {
        return this.type;
      }
    },
    inputMode() {
      if (this.type === 'decimal') {
        return 'decimal';
      }
      if (this.type === 'number' || this.type === 'int') {
        return 'numeric';
      }
      return null;
    },
    inputStyle() {
      const style = {
        '--input-width': 'minmax(0, 1fr)',
      };
      if (
        this.disabled &&
        this.displayLockIfDisabled &&
        !(this.isDate || this.isDateTime)
      ) {
        style['--input-left-icon-width'] = '2rem';
      } else {
        style['--input-left-icon-width'] = '0';
      }
      if (!this.extraSlotVisible && !this.hasSuffixIcon) {
        style['--input-right-icon-width'] = '0';
        style['--input-extra-slot-width'] = '0';
        return style;
      } else if (!this.extraSlotVisible && this.hasSuffixIcon) {
        style['--input-right-icon-width'] = '2rem';
        style['--input-extra-slot-width'] = '0';
        return style;
      } else if (this.extraSlotVisible && !this.hasSuffixIcon) {
        style['--input-right-icon-width'] = '0';
        style['--input-extra-slot-width'] = this.extraSlotSize;
        return style;
      } else if (this.extraSlotVisible && this.hasSuffixIcon) {
        style['--input-right-icon-width'] = '2rem';
        style['--input-extra-slot-width'] = this.extraSlotSize;
        return style;
      }
      return style;
    },
    extraSlotVisible() {
      return !!this.$slots['extra'];
    },
  },
  mounted() {
    this.inputValue = this.initValue(this.value, true);
    if (this.isDate || this.isDateTime) {
      this.lastCommittedDateInput = this.inputValue || null;
    }
    window.addEventListener('keydown', this.setZeroKeystroke);
    if (this.instantFocus && this.responsiveBreakpointMd && this.$refs.input) {
      this.$refs.input.focus();
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.setZeroKeystroke);
  },
  methods: {
    setZeroKeystroke(event) {
      this.userTypedZero = event.key === '0';
    },
    toDateObject(value) {
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
      }
      if (typeof value === 'string' && value) {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
      }
      return null;
    },
    onInput: function ($event) {
      if (this.disabled) return;
      const rawValue = this.keepAllowedCharacters($event.target.value);
      if (rawValue !== $event.target.value) {
        $event.target.value = rawValue;
      }
      if (
        (this.isDate || this.isDateTime) &&
        this.isFocussed &&
        rawValue !== (this.lastCommittedDateInput || '')
      ) {
        this.dateUserEdited = true;
      }
      this.inputValue = rawValue;
      this.fireInputInput(this.valueToReturn(rawValue));
      $event.stopImmediatePropagation();
    },
    commitDateChange(rawValue) {
      if (rawValue === this.lastCommittedDateInput) return;
      if (
        rawValue === '' &&
        this.lastCommittedDateInput &&
        !this.dateUserEdited
      ) {
        this.inputValue = this.lastCommittedDateInput;
        this.$nextTick(() => {
          if (this.$refs.input && this.lastCommittedDateInput) {
            this.$refs.input.value = this.lastCommittedDateInput;
          }
        });
        return;
      }
      this.lastCommittedDateInput = rawValue || null;
      const newValue = this.valueToReturn(rawValue);
      this.fireInputChange(newValue);
      this.inputValue = this.initValue(newValue);
      this.dateUserEdited = false;
    },
    onChange($event) {
      if (this.disabled) return;
      if (this.isDate || this.isDateTime) {
        this.commitDateChange($event.target.value);
        $event.stopImmediatePropagation();
        return;
      }

      const rawValue = this.keepAllowedCharacters($event.target.value);
      if (rawValue !== $event.target.value) {
        $event.target.value = rawValue;
      }
      const newValue = this.valueToReturn(rawValue);
      this.fireInputChange(newValue);
      this.inputValue = this.initValue(newValue);
      if (this.resetAfterChange) {
        $event.target.value = null;
      }
      $event.stopImmediatePropagation();
    },
    onKeydown($event) {
      if (this.disabled) {
        // Allow tab/shortcuts; block typing when visually readonly-as-disabled
        if (
          !['Tab', 'Escape', 'Shift', 'Control', 'Alt', 'Meta'].includes(
            $event.key,
          )
        ) {
          $event.preventDefault();
        }
      }
    },
    onPasswordVisibilitySwitch() {
      this.passwordIsVisible = !this.passwordIsVisible;
      this.$emit('passwordVisibilityChange', this.passwordIsVisible);
    },
    onFocus($event) {
      this.$emit('focus');
      $event.stopImmediatePropagation();
      if (this.validationState) {
        this.validationState.$reset();
      }
      this.isFocussed = true;
    },
    getDecimalSeparator(valueToCheck) {
      if (!valueToCheck) return null;
      const lastDotIndex = valueToCheck.lastIndexOf('.');
      const lastCommaIndex = valueToCheck.lastIndexOf(',');
      if (lastDotIndex === -1 && lastCommaIndex === -1) return null;
      return lastDotIndex > lastCommaIndex ? '.' : ',';
    },
    initValue(valueToUse, storeInitialDecimalPlaces = false) {
      if (this.type === 'decimal') {
        if (valueToUse === 'NaN') return 'NaN';
        if (!valueToUse) return '';

        if (valueToUse instanceof Decimal) {
          return valueToUse.toFixed(this.inputDecimalPlaces);
        } else if (typeof valueToUse === 'number') {
          return this.n(valueToUse, `decimal${this.inputDecimalPlaces}Digits`);
        }

        let decimalPlaces;
        const separator = this.getDecimalSeparator(valueToUse);
        let typedDecimalPlaces = 0;
        if (separator) {
          try {
            typedDecimalPlaces = valueToUse.split(separator)[1].length;
          } catch {
            typedDecimalPlaces = 0;
          }
        }
        if (storeInitialDecimalPlaces) {
          decimalPlaces = typedDecimalPlaces;
          this.inputDecimalPlaces = decimalPlaces;
        } else {
          // Keep digits the user just typed. With 0 stored places, 1.5 was
          // formatted as a whole number and rounded to 2.
          decimalPlaces = Math.max(this.inputDecimalPlaces, typedDecimalPlaces);
        }
        const numberFormatting = `decimal${decimalPlaces}Digits`;
        return this.n(
          parseFloat(valueToUse.replace(separator, '.')),
          numberFormatting,
        );
      }

      if (valueToUse || valueToUse === 0) {
        const dateValue = this.toDateObject(valueToUse);
        if (dateValue) {
          if (this.isDate) {
            return dateValue.toISOString().split('T')[0];
          }
          if (this.isDateTime) {
            const date = new Date(
              dateValue.getTime() - dateValue.getTimezoneOffset() * 60000,
            ).toISOString();
            return this.showSeconds ? date.slice(0, 19) : date.slice(0, 16);
          }
        }
        return valueToUse;
      }
      return '';
    },
    valueToReturn(value) {
      /** Empty values are returned as null */
      if (value === '') {
        return (this.isDate || this.isDateTime) && this.userTypedZero
          ? 'DATE_NOT_RESET'
          : null;
      }

      /** For non empty values, we need to convert the value into the correct type */
      if (['number', 'int', 'decimal'].includes(this.type)) {
        // i18n number formatting can introduce non-breaking spaces (e.g. fr-BE thousands separator)
        value = value.replace(/[\s\u00A0\u202F]/g, '');
      }
      if (this.type === 'number') {
        value = parseFloat(value.replace(',', '.'));
      } else if (this.type === 'int') {
        // Still parse as float to allow validations
        if (value.includes('.')) {
          value = parseFloat(value);
        } else {
          value = parseInt(value);
        }
      } else if (this.type === 'datetime-local') {
        return new Date(value);
      }
      return value;
    },
    onRightIconClick() {
      this.$emit('rightIconClick');
    },
    openUrl() {
      const raw = String(this.inputValue ?? this.value ?? '').trim();
      if (!raw) return;
      const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    keepAllowedCharacters(value) {
      if (!this.allowedCharacters || value == null) return value;
      return String(value).replace(
        new RegExp(`[^${this.allowedCharacters}]`, 'g'),
        '',
      );
    },
    onBlur(event) {
      if (this.isDate || this.isDateTime) {
        this.commitDateChange(event.target.value);
      }
      this.isFocussed = false;
      if (this.type === 'password') {
        this.inputValue = this.initValue(this.value, true);
      }
      // Re-show validation messages after leaving the field if it is still invalid.
      if (this.validationState) {
        this.validationState.$touch();
      }
      this.$emit('blur', event.target.value);
    },
    removeSeparatorsForEdition(value) {
      if (!value) return '';
      const lastDotIndex = value.lastIndexOf('.');
      const lastCommaIndex = value.lastIndexOf(',');
      const decimalSeparator =
        lastDotIndex > lastCommaIndex
          ? '.'
          : lastCommaIndex > lastDotIndex
            ? ','
            : null;
      const groupingSeparator =
        decimalSeparator === '.' ? ',' : decimalSeparator === ',' ? '.' : null;
      const valueWithoutGroupingSeparator = groupingSeparator
        ? value.replaceAll(groupingSeparator, '')
        : value;
      return valueWithoutGroupingSeparator.replace(/[\s\u00A0\u202F]/g, '');
    },
    resetToOrignalData() {
      this.fireInputChange(this.originalData);
    },
  },

  watch: {
    value(newValue) {
      if (this.isFocussed && newValue !== '' && newValue != null) {
        return;
      }
      this.inputValue = this.initValue(newValue, true);
      if (this.isDate || this.isDateTime) {
        this.lastCommittedDateInput = this.inputValue || null;
        this.dateUserEdited = false;
      }
    },
    isFocussed(newValue) {
      if (newValue && this.type === 'decimal') {
        this.inputValue = this.removeSeparatorsForEdition(this.inputValue);
      }
      if (newValue && this.selectEntireContentOnFocus) {
        setTimeout(() => {
          if (this.$refs.input) {
            this.$refs.input.select();
          }
        }, 50);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.input {
  grid-template-columns:
    var(--input-left-icon-width) var(--input-width) var(
      --input-right-icon-width
    )
    var(--input-extra-slot-width);
}

[type='text'],
[type='email'],
[type='url'],
[type='password'],
[type='number'],
[type='date'],
[type='datetime-local'],
[type='month'],
[type='search'],
[type='tel'],
[type='time'],
[type='decimal'],
[type='standard'],
[type='week'] {
  font-size: inherit;
  padding-right: 0.4rem;
  /* Prefer shared --hit-floating-inset-x when inside a floating field */
  padding-left: var(
    --hit-floating-value-inset-x,
    var(--hit-floating-inset-x, 0.3rem)
  );
  @apply py-0;
}

/* Hide native date/datetime text until the floating label is lifted. */
[type='date'].text-transparent,
[type='datetime-local'].text-transparent {
  color: transparent;
  caret-color: transparent;

  &::-webkit-datetime-edit,
  &::-webkit-datetime-edit-year-field,
  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-day-field,
  &::-webkit-datetime-edit-hour-field,
  &::-webkit-datetime-edit-minute-field,
  &::-webkit-datetime-edit-second-field,
  &::-webkit-datetime-edit-ampm-field {
    color: transparent;
  }

  &::placeholder {
    color: transparent;
  }
}

/* type=int renders as number; disabled is applied as readonly, so spinners stay. */
.hide-number-spinners {
  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
